import React, { useCallback, useState } from 'react';
import { Form, Input, DatePicker, Select, Switch, Button, Dropdown, Checkbox, Radio } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import moment from 'moment';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
	locationsRestCrudThunksForBuyer,
	locationsRestCrudThunksForSupplier,
} from '../../thunks/locations_thunks';
import {
	assetTypesRestCrudThunksForBuyer,
	assetTypesRestCrudThunksForSupplier,
} from '../../thunks/asset_types_thunks';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { encodeUTCAdjustedDate } from '../../utils/DataFormatterUtils';
import SelectWithAllToggle from '../common/SelectWithAllToggle';
import RegionsLocationsTreeSelect from '../regions_tree_select/RegionsLocationsTreeSelect';
import {
	cycleEventsRestCrudThunksForBuyer,
	cycleEventsRestCrudThunksForSupplier,
} from '../../thunks/cycle_events_thunks';
import { ASSET_FIELDS_DETAILS, ASSET_FIELDS_LABELS_MAP } from '../../utils/assetUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';

interface AssetCycleRunFormProps {
	formData?: any;
	isEditForm?: boolean;
	onSuccess?: (record: any) => void;
	history?: any;
	currentUser?: any;
	locations?: any;
	fetchLocations?: any;
	fetchMultipleLocations?: any;
	assetTypes?: any;
	fetchAssetTypes?: any;
	fetchMultipleAssetTypes?: any;
	fetching?: boolean;
	create?: any;
	update?: any;
	dispatch?: any;
}

const ASSET_CYCLE_RUN_FORM_LOCATIONS_AUTOCOMPLETE = 'ASSET_CYCLE_RUN_FORM_LOCATIONS_AUTOCOMPLETE';
const ASSET_CYCLE_RUN_FORM_ASSET_TYPES_AUTOCOMPLETE =
	'ASSET_CYCLE_RUN_FORM_ASSET_TYPES_AUTOCOMPLETE';

const AssetCycleRunForm: React.FC<AssetCycleRunFormProps> = ({
	formData = {},
	isEditForm = false,
	onSuccess,
	history,
	currentUser,
	locations,
	fetchLocations,
	fetchMultipleLocations,
	assetTypes,
	fetchAssetTypes,
	fetchMultipleAssetTypes,
	fetching,
	create,
	update,
	dispatch,
}) => {
	const [form] = Form.useForm();
	const [submitting, setSubmitting] = useState(false);
	const [dropdownVisible, setDropdownVisible] = useState(false);

	const dateFormat = 'MM/DD';

	const handleSubmit = useCallback(
		(values) => {
			setSubmitting(true);

			// Transform the form values to match the expected API structure
			const cycleRunData = {
				...(formData || {}),
				allowQROverridesByTechs: values.allowQROverridesByTechs,
				prefillFields: values.prefillFields,
				name: values.name,
				dueAt: values.dueAt && encodeUTCAdjustedDate(values.dueAt),
				locationIds: values.allLocationSelected ? [] : values.locationIds || [],
				assetTypeIds: values.allAssetTypeSelected ? [] : values.assetTypeIds || [],
				captureFields: values.captureFields || [],
			};

			const submitAction = isEditForm ? update : create;

			if (submitAction) {
				submitAction(cycleRunData)
					.then((record) => {
						if (onSuccess) {
							onSuccess(record);
						}
					})
					.catch((err) => {
						console.error('Error submitting form:', err);
					})
					.finally(() => {
						setSubmitting(false);
					});
			} else {
				setSubmitting(false);
			}
		},
		[formData, isEditForm, onSuccess, create, update]
	);

	const handleCancel = useCallback(() => {
		if (history) {
			history.goBack();
		}
	}, [history]);

	return (
		<Form
			form={form}
			layout="vertical"
			initialValues={{
				...formData,
				dueAt: formData && formData.dueAt ? moment(formData.dueAt) : undefined,
				locationIds: (formData && formData.locationIds) || [],
				assetTypeIds: (formData && formData.assetTypeIds) || [],
				allLocationSelected:
					formData && Array.isArray(formData.locationIds) && formData.locationIds.length === 0,
				allAssetTypeSelected:
					formData && Array.isArray(formData.assetTypeIds) && formData.assetTypeIds.length === 0,
			}}
			onFinish={handleSubmit}
			style={{ maxWidth: 640 }}
		>
			<Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
				<Input placeholder="Enter name" />
			</Form.Item>

			<Form.Item
				name="dueAt"
				label="Due Date"
				rules={[{ required: true, message: 'Due Date is required' }]}
			>
				<DatePicker
					style={{ width: '100%' }}
					format={dateFormat}
					placeholder="Select due date"
					disabledDate={(current) => current && current < moment().startOf('day')}
				/>
			</Form.Item>

			<SelectWithAllToggle
				label="Locations"
				name="locationIds"
				allName="allLocationSelected"
				form={form}
				required={true}
				displaySelectAllWithLabel
			>
				<RegionsLocationsTreeSelect
					mode="multiple"
					noIsActiveFilter
					filtersSlice={locations}
					targetCollectionName={ASSET_CYCLE_RUN_FORM_LOCATIONS_AUTOCOMPLETE}
				/>
			</SelectWithAllToggle>

			<SelectWithAllToggle
				label="Asset Type"
				name="assetTypeIds"
				allName="allAssetTypeSelected"
				form={form}
				required={true}
				displaySelectAllWithLabel
			>
				<OWAsyncSelect
					mode="multiple"
					stateSlice={assetTypes}
					targetCollectionName={ASSET_CYCLE_RUN_FORM_ASSET_TYPES_AUTOCOMPLETE}
					fetchMultiple={(ids, targetCollectionName) => {
						fetchMultipleAssetTypes(ids, targetCollectionName);
					}}
					fetchData={(
						searchText,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
					) => {
						fetchAssetTypes(
							{ name: searchText || undefined },
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						);
					}}
					placeholder="Select asset types"
					renderRecord={(assetType) => (
						<Select.Option key={assetType.id} value={assetType.id}>
							{assetType.name}
						</Select.Option>
					)}
					sortBy={{ sort_by: 'name', order: 'ascend' }}
				/>
			</SelectWithAllToggle>

			<div className="mb-4">
				<Form.Item
					name="allowQROverridesByTechs"
					label="Allow field technicians to perform QR code overrides?"
					required
					help="Allow techs to mark QR codes to be reassigned or swapped"
					rules={[{ required: true, message: 'Please, select an option' }]}
				>
					<Radio.Group>
						<Radio value={true}>Yes</Radio>
						<Radio value={false}>No</Radio>
					</Radio.Group>
				</Form.Item>
			</div>

			<div className="my-4">
				<div className="bodymedium  text-character-primary">Fields Captured:</div>
				<div className="bodyregular text-character-secondary">
					The fields below will be captured for all logged assets.
				</div>
			</div>

			<div className="mb-4">
				<Form.Item
					name="prefillFields"
					label="Prefill all fields captured with existing scanned data?"
					required
					help="The fields below will be captured for all logged assets."
					className="mb-4"
					rules={[{ required: true, message: 'Please, select an option' }]}
				>
					<Radio.Group
						options={[
							{ label: 'Yes', value: true },
							{ label: 'No, user must enter data', value: false },
						]}
						value={form.getFieldValue('prefillFields')}
						onChange={(e) => form.setFieldsValue({ prefillFields: e.target.value })}
					/>
				</Form.Item>
			</div>

			<Form.List name="captureFields">
				{(fields, { add, remove }) => {
					const currentFieldKeys = fields.map((field) => {
						const fieldValue = form.getFieldValue(['captureFields', field.name, 'key']);
						return fieldValue;
					});

					const handleDropdownVisibleChange = (visible: boolean) => {
						setDropdownVisible(visible);
					};

					const dropdownMenu = (
						<div
							style={{
								background: 'white',
								padding: '8px 0',
								minWidth: 200,
								overflowY: 'auto',
								maxHeight: '50vh',
							}}
						>
							{ASSET_FIELDS_DETAILS.map((field) => {
								const isSelected = currentFieldKeys.includes(field.key);
								return (
									<div
										key={field.key}
										style={{
											padding: '8px 16px',
											cursor: 'pointer',
										}}
									>
										<Checkbox
											checked={isSelected}
											onChange={(e) => {
												const checked = e.target.checked;

												if (checked && !isSelected) {
													add({ key: field.key, type: field.label, isRequired: false });
												}

												if (!checked && isSelected) {
													const indexToRemove = fields.findIndex((fieldItem) => {
														const fieldValue = form.getFieldValue([
															'captureFields',
															fieldItem.name,
															'key',
														]);
														return fieldValue === field.key;
													});

													if (indexToRemove !== -1) {
														remove(indexToRemove);
													}
												}
											}}
										>
											{field.label}
										</Checkbox>
									</div>
								);
							})}
						</div>
					);

					return (
						<>
							{fields.map(({ key, name, ...restField }) => (
								<div key={key} className="mb-4" style={{ borderBottom: '1px solid #f0f0f0' }}>
									<Form.Item {...restField} name={[name, 'key']} hidden>
										<Input type="hidden" />
									</Form.Item>
									<Form.Item {...restField} name={[name, 'type']} hidden>
										<Input type="hidden" />
									</Form.Item>
									<div style={{ borderBottom: '1px solid #f0f0f0', padding: '16px 0' }}>
										<div
											style={{
												display: 'flex',
												width: '100%',
												justifyContent: 'space-between',
												alignItems: 'center',
											}}
										>
											<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
												<span className="bodyregular text-character-primary">
													{
														ASSET_FIELDS_LABELS_MAP[
															form.getFieldValue(['captureFields', name, 'key'])
														]
													}
												</span>
											</div>
											<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
												<span className="bodyregular text-character-primary">Input Mandatory?</span>
												<Form.Item
													name={[name, 'isRequired']}
													valuePropName="checked"
													noStyle
													initialValue={false}
												>
													<Switch size="small" />
												</Form.Item>
											</div>
										</div>
									</div>
								</div>
							))}
							<Form.Item>
								<Dropdown
									overlay={dropdownMenu}
									visible={dropdownVisible}
									onVisibleChange={handleDropdownVisibleChange}
									trigger={['click']}
								>
									<Button type="default" icon={<DownOutlined />}>
										Add/Remove Fields
									</Button>
								</Dropdown>
							</Form.Item>
						</>
					);
				}}
			</Form.List>

			<Form.Item style={{ marginTop: 24 }}>
				<Button type="primary" htmlType="submit" loading={submitting} style={{ marginRight: 16 }}>
					{isEditForm ? 'Update Event' : 'Create Event'}
				</Button>
				<Button onClick={handleCancel}>Cancel</Button>
			</Form.Item>
		</Form>
	);
};

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
	locations: state.locations,
	assetTypes: state.asset_types,
	fetching: state.cycle_events.fetching,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	create: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? cycleEventsRestCrudThunksForSupplier.create(entity)
				: cycleEventsRestCrudThunksForBuyer.create(entity)
		),
	update: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? cycleEventsRestCrudThunksForSupplier.update(entity)
				: cycleEventsRestCrudThunksForBuyer.update(entity)
		),
	fetchLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationsRestCrudThunksForSupplier.readLite(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: locationsRestCrudThunksForBuyer.readLite(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchMultipleLocations: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? locationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: locationsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
	fetchAssetTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetTypesRestCrudThunksForSupplier.readLite(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: assetTypesRestCrudThunksForBuyer.readLite(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	fetchMultipleAssetTypes: (ids, targetCollectionName) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? assetTypesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
				: assetTypesRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)
		),
});

const ConnectedAssetCycleRunForm = withUserType(
	connect(mapStateToProps, mapDispatchToProps)(AssetCycleRunForm as any)
);
export default withRouter<any, any>(ConnectedAssetCycleRunForm);
